/**
 * InlineBanner — the row/field-level notice (see DESIGN-SYSTEM.md "Inline banners").
 *
 * Banner's smaller sibling: same variant semantics, same "the variant fixes
 * the aria role so it cannot drift" rule, no box. It is one line of text
 * hanging off the control it is about — under an input, or under the Item
 * cell of a table row — where a bordered, tinted strip would blow the grid
 * out.
 *
 * Placement, not margin classes, is the caller's only spacing choice: a
 * notice under a form field and a notice inside a table cell were drifting
 * apart one `mt-0.5`/`px-2` at a time across ~35 hand-rolled <p> tags.
 */
import type { ReactNode } from "react";
import { cx } from "@shared/cx";
import styles from "./InlineBanner.module.css";

export type InlineBannerVariant = "info" | "warning" | "error";

// The variant class owns its colour and its weight outright — a caller can
// no more tint one than it could before, and CSS Modules do not merge, so a
// class passed alongside would win or lose by source order rather than by
// intent (DESIGN-SYSTEM.md: a variant that bakes in a colour must not also accept
// one).
const VARIANTS: Record<InlineBannerVariant, string> = {
  error: styles.error,
  warning: styles.warning,
  info: styles.info,
};

const PLACEMENTS = {
  /** Under a form field — the input's own label/field rhythm. */
  field: styles.field,
  /** Inside a table cell, aligned with the cell input's text. */
  cell: styles.cell,
  /** A grid/flex child whose parent already owns the gap. */
  none: undefined,
} as const;

export function InlineBanner({
  variant,
  placement = "field",
  children,
  className,
  "data-testid": testId,
}: {
  variant: InlineBannerVariant;
  placement?: keyof typeof PLACEMENTS;
  children: ReactNode;
  /** Extra classes only — never a colour or a margin (nothing merges). */
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <p
      // error announces assertively; the advisory variants announce politely,
      // matching Banner exactly.
      role={variant === "error" ? "alert" : "status"}
      // The marker scrollToFirstBlockingNotice finds a blocked action's first
      // error by. It is on every variant so the attribute means "an inline
      // banner of this kind", not "a scroll target" — nothing else may key
      // behaviour off the advisory ones. It is also what the component's
      // guard test recognises a real notice by, now that the look is a
      // hashed class name rather than a readable class string.
      data-inline-banner={variant}
      data-testid={testId}
      className={cx(
        styles.notice,
        VARIANTS[variant],
        PLACEMENTS[placement],
        className,
      )}
    >
      {children}
    </p>
  );
}
